/* ==========================================================================
   Emberly — core theme script (deferred, ~11KB gz)
   --------------------------------------------------------------------------
   1. helpers            — qs, on, focus trap, live region
   2. reveal             — IntersectionObserver for .em-reveal (fails open)
   3. announcement bar   — cross-fade rotation
   4. header             — sticky group, hide on scroll-down
   5. menu drawer
   6. cart               — EmCart API + <cart-drawer> element (Section Rendering API)
   7. scrollers & dots   — generic horizontal snap scrollers
   8. accordions         — optional single-open groups
   9. heat-level widget  — shared by the features + product sections
   Every initialiser is idempotent and re-runs on shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';
  var existing = window.Emberly || {};
  if (existing.init) return;

  var doc = document;
  var html = doc.documentElement;
  html.classList.add('em-js');

  /* 1. helpers -------------------------------------------------------------- */
  function qs(sel, root) { return (root || doc).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { el && el.addEventListener(evt, fn, opts || false); }
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var trap = { container: null, handler: null, restore: null };
  function trapFocus(container, first) {
    releaseFocus();
    trap.container = container;
    trap.restore = doc.activeElement;
    var items = qsa(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
    trap.handler = function (e) {
      if (e.key !== 'Tab') return;
      items = qsa(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var firstEl = items[0], lastEl = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && doc.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    on(doc, 'keydown', trap.handler);
    var target = first || items[0] || container;
    setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (err) { /* noop */ } }, 30);
  }
  function releaseFocus() {
    if (trap.handler) doc.removeEventListener('keydown', trap.handler);
    if (trap.restore && trap.restore.focus) { try { trap.restore.focus({ preventScroll: true }); } catch (err) { /* noop */ } }
    trap.container = null; trap.handler = null; trap.restore = null;
  }
  var lockCount = 0;
  function lockBody(lock) {
    lockCount = Math.max(0, lockCount + (lock ? 1 : -1));
    doc.body.classList.toggle('em-lock', lockCount > 0);
  }
  var liveEl;
  function announce(msg) {
    if (!liveEl) {
      liveEl = doc.createElement('div');
      liveEl.className = 'em-vh';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      doc.body.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = msg; }, 50);
  }
  function money(cents) {
    var fmt = (window.Emberly && window.Emberly.moneyFormat) || '${{amount}}';
    var n = (cents / 100).toFixed(2);
    var parts = n.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100)).replace(/\{\{\s*amount\s*\}\}/, parts.join('.'));
    return out.replace(/<[^>]+>/g, '');
  }

  /* 2. reveal --------------------------------------------------------------- */
  var revealIO;
  function initReveal(scope) {
    var els = qsa('.em-reveal:not([data-em-observed])', scope);
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion) {
      els.forEach(function (el) { el.classList.add('is-in'); el.setAttribute('data-em-observed', ''); });
      return;
    }
    revealIO = revealIO || new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { el.setAttribute('data-em-observed', ''); revealIO.observe(el); });
  }

  /* 3. announcement bar ----------------------------------------------------- */
  function initAnnouncement(scope) {
    qsa('[data-em-announcement]:not([data-em-init])', scope).forEach(function (bar) {
      bar.setAttribute('data-em-init', '');
      var items = qsa('.em-ann__item', bar);
      if (items.length < 2) return;
      var i = 0, timer;
      var interval = parseInt(bar.getAttribute('data-interval'), 10) || 0;
      function show(n) {
        i = (n + items.length) % items.length;
        items.forEach(function (it, k) {
          it.classList.toggle('is-active', k === i);
          it.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        });
      }
      function start() { if (interval > 0 && !reduceMotion) { stop(); timer = setInterval(function () { show(i + 1); }, interval * 1000); } }
      function stop() { if (timer) clearInterval(timer); }
      on(qs('[data-em-ann-prev]', bar), 'click', function () { show(i - 1); start(); });
      on(qs('[data-em-ann-next]', bar), 'click', function () { show(i + 1); start(); });
      on(bar, 'mouseenter', stop);
      on(bar, 'mouseleave', start);
      on(bar, 'focusin', stop);
      on(bar, 'focusout', start);
      start();
    });
  }

  /* 4. header --------------------------------------------------------------- */
  function initHeader() {
    var wrap = qs('[data-em-header]');
    if (!wrap || wrap.hasAttribute('data-em-init')) return;
    wrap.setAttribute('data-em-init', '');
    // The header lives inside [data-em-header-group] together with the
    // announcement bar. Older builds wrapped it in a Shopify section group.
    var section = wrap.closest('.shopify-section') || wrap;
    var group = wrap.closest('[data-em-header-group]') ||
      (wrap.closest('.shopify-section') ? wrap.closest('.shopify-section').parentElement : null);
    var hideOnScroll = wrap.getAttribute('data-hide-on-scroll') === 'true';

    // Stick the whole group (announcement + header) with a negative top equal to
    // the announcement height, so the bar scrolls away and the header stays.
    function measure() {
      var h = wrap.offsetHeight;
      html.style.setProperty('--em-header-h-live', h + 'px');
      // height of everything above the header inside the group (announcement bar)
      var annH = 0, sib = section.previousElementSibling;
      while (sib) { annH += sib.offsetHeight; sib = sib.previousElementSibling; }
      html.style.setProperty('--em-ann-h', Math.max(0, annH) + 'px');
      if (group && group !== doc.body && group !== html) {
        group.style.position = 'sticky';
        group.style.top = 'calc(var(--em-ann-h, 0px) * -1)';
        group.style.zIndex = '60';
      } else {
        section.style.position = 'sticky';
        section.style.top = '0';
        section.style.zIndex = '60';
      }
    }
    measure();
    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(section);
      if (group) new ResizeObserver(measure).observe(group);
    }
    on(window, 'resize', measure);

    var lastY = window.pageYOffset, ticking = false, hidden = false, stuck = false;
    function update() {
      var y = window.pageYOffset;
      var annH = parseInt(html.style.getPropertyValue('--em-ann-h'), 10) || 0;
      var isStuck = y > annH + 2;
      if (isStuck !== stuck) { stuck = isStuck; wrap.classList.toggle('is-stuck', stuck); }
      if (hideOnScroll) {
        var delta = y - lastY;
        var menuOpen = doc.body.classList.contains('em-lock');
        if (!menuOpen) {
          if (delta > 6 && y > wrap.offsetHeight + annH + 40 && !hidden) { hidden = true; wrap.classList.add('is-hidden'); }
          else if ((delta < -6 || y <= annH) && hidden) { hidden = false; wrap.classList.remove('is-hidden'); }
        }
      }
      lastY = y;
      ticking = false;
    }
    on(window, 'scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
    Emberly.showHeader = function () { hidden = false; wrap.classList.remove('is-hidden'); };
  }

  /* 5. menu drawer ---------------------------------------------------------- */
  function initMenu() {
    var menu = qs('[data-em-menu]');
    if (!menu || menu.hasAttribute('data-em-init')) return;
    menu.setAttribute('data-em-init', '');
    var panel = qs('.em-menu__panel', menu);
    var openBtn = qs('[data-em-menu-open]');
    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      lockBody(true);
      trapFocus(panel, qs('.em-menu__close', menu));
    }
    function close() {
      if (!menu.classList.contains('is-open')) return;
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      lockBody(false);
      releaseFocus();
    }
    on(openBtn, 'click', open);
    qsa('[data-em-menu-close]', menu).forEach(function (el) { on(el, 'click', close); });
    on(doc, 'keydown', function (e) { if (e.key === 'Escape' && menu.classList.contains('is-open')) close(); });
    qsa('[data-em-menu-toggle]', menu).forEach(function (btn) {
      on(btn, 'click', function () {
        var item = btn.closest('.em-menu__item');
        var isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
    Emberly.menu = { open: open, close: close };
  }

  /* 6. cart ----------------------------------------------------------------- */
  var SECTIONS = ['emberly-cart-drawer', 'emberly-cart-bubble'];
  function routes() { return window.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart' }; }
  function jsonHeaders() { return { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }; }

  function parseSection(htmlText, selector) {
    var d = new DOMParser().parseFromString(htmlText, 'text/html');
    return selector ? d.querySelector(selector) : d;
  }

  function renderSections(sections) {
    if (!sections) return;
    var drawer = qs('#EmCart');
    if (drawer && sections['emberly-cart-drawer']) {
      var fresh = parseSection(sections['emberly-cart-drawer'], '[data-em-cart]');
      var freshPanel = fresh && fresh.querySelector('[data-em-cart-panel]');
      var panel = qs('[data-em-cart-panel]', drawer);
      if (freshPanel && panel) {
        panel.innerHTML = freshPanel.innerHTML;
        drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      }
    }
    if (sections['emberly-cart-bubble']) {
      var freshCount = parseSection(sections['emberly-cart-bubble'], '[data-em-cart-count]');
      var current = qs('[data-em-cart-count]');
      if (freshCount && current) {
        var before = current.getAttribute('data-count');
        current.replaceWith(freshCount);
        if (before !== freshCount.getAttribute('data-count') && !reduceMotion) {
          freshCount.classList.add('is-bumping');
          setTimeout(function () { freshCount.classList.remove('is-bumping'); }, 500);
        }
      }
    }
  }

  function cartError(message, lineEl) {
    var target = lineEl ? qs('[data-em-line-error]', lineEl) : qs('[data-em-cart-errors]');
    if (target) target.textContent = message;
    else announce(message);
  }

  function request(url, body) {
    return fetch(url, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); });
  }

  var EmCart = {
    busy: false,
    /** add([{id, quantity, properties}]) or add(id, qty, props) */
    add: function (items, qty, props) {
      var list = Array.isArray(items) ? items : [{ id: parseInt(items, 10), quantity: qty || 1, properties: props || undefined }];
      list = list.map(function (it) { var o = { id: it.id, quantity: it.quantity || 1 }; if (it.properties && Object.keys(it.properties).length) o.properties = it.properties; if (it.selling_plan) o.selling_plan = it.selling_plan; return o; });
      return request(routes().cart_add_url, { items: list, sections: SECTIONS.join(','), sections_url: window.location.pathname })
        .then(function (res) {
          if (!res.ok || res.data.status) {
            var msg = (res.data && (res.data.description || res.data.message)) || (window.cartStrings && window.cartStrings.error) || 'Could not add to cart.';
            throw new Error(msg);
          }
          renderSections(res.data.sections);
          EmCart.emit('add', res.data);
          announce((window.Emberly.strings && window.Emberly.strings.added) || 'Added to cart');
          return res.data;
        });
    },
    change: function (line, quantity, lineEl) {
      if (lineEl) lineEl.classList.add('is-busy');
      return request(routes().cart_change_url, { line: line, quantity: quantity, sections: SECTIONS.join(','), sections_url: window.location.pathname })
        .then(function (res) {
          if (!res.ok || res.data.errors) {
            var msg = (res.data && (res.data.errors || res.data.description || res.data.message)) || (window.cartStrings && window.cartStrings.error) || 'Could not update the cart.';
            cartError(typeof msg === 'string' ? msg : String(msg), lineEl);
            if (lineEl) lineEl.classList.remove('is-busy');
            return res.data;
          }
          renderSections(res.data.sections);
          EmCart.emit('change', res.data);
          return res.data;
        })
        .catch(function (e) { if (lineEl) lineEl.classList.remove('is-busy'); cartError(e.message || 'Could not update the cart.', lineEl); });
    },
    note: function (note) {
      return request(routes().cart_update_url, { note: note });
    },
    refresh: function () {
      return fetch(routes().cart_url + '?sections=' + SECTIONS.join(','), { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (sections) { renderSections(sections); });
    },
    open: function () { var d = qs('#EmCart'); if (d && d.open) d.open(); },
    close: function () { var d = qs('#EmCart'); if (d && d.close) d.close(); },
    emit: function (type, data) {
      doc.dispatchEvent(new CustomEvent('em:cart:' + type, { detail: data }));
      doc.dispatchEvent(new CustomEvent('em:cart:updated', { detail: { type: type, cart: data } }));
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'emberly', cartData: data });
        }
      } catch (err) { /* pubsub optional */ }
    }
  };

  /* <cart-drawer> element — API-compatible with Dawn's product-form.js */
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class extends HTMLElement {
      connectedCallback() {
        if (this.hasAttribute('data-em-init')) return;
        this.setAttribute('data-em-init', '');
        var self = this;
        on(this, 'click', function (e) {
          var t = e.target;
          if (t.closest('[data-em-cart-close]')) { e.preventDefault(); self.close(); return; }
          var step = t.closest('[data-em-qty-step]');
          if (step) {
            var input = qs('[data-em-qty-input]', step.parentElement);
            var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-em-qty-step'), 10));
            input.value = next;
            self.changeLine(input);
            return;
          }
          var rm = t.closest('[data-em-remove]');
          if (rm) { EmCart.change(parseInt(rm.getAttribute('data-line'), 10), 0, rm.closest('[data-em-line]')); return; }
          var add = t.closest('[data-em-add]');
          if (add) {
            add.classList.add('is-loading');
            EmCart.add(parseInt(add.getAttribute('data-em-add'), 10), parseInt(add.getAttribute('data-em-add-qty'), 10) || 1)
              .catch(function (err) { cartError(err.message); })
              .then(function () { add.classList.remove('is-loading'); });
          }
        });
        var noteTimer;
        on(this, 'input', function (e) {
          if (e.target.hasAttribute('data-em-cart-note')) {
            clearTimeout(noteTimer);
            noteTimer = setTimeout(function () { EmCart.note(e.target.value); }, 400);
          }
        });
        on(this, 'change', function (e) {
          if (e.target.hasAttribute('data-em-qty-input')) self.changeLine(e.target);
        });
        on(doc, 'keydown', function (e) { if (e.key === 'Escape' && self.classList.contains('is-open')) self.close(); });
      }
      changeLine(input) {
        var line = parseInt(input.getAttribute('data-line'), 10);
        var qty = Math.max(0, parseInt(input.value, 10) || 0);
        EmCart.change(line, qty, input.closest('[data-em-line]'));
      }
      open(trigger) {
        if (this.classList.contains('is-open')) return;
        this.trigger = trigger;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        lockBody(true);
        trapFocus(qs('[data-em-cart-panel]', this), qs('.em-cart__close', this));
        if (typeof Emberly.showHeader === 'function') Emberly.showHeader();
      }
      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        lockBody(false);
        releaseFocus();
      }
      /* Dawn compatibility (product-form.js, quick-add.js, standard-actions) */
      setActiveElement(el) { this.trigger = el; }
      getSectionsToRender() { return SECTIONS.map(function (id) { return { id: id }; }); }
      renderContents(parsedState) {
        renderSections(parsedState && parsedState.sections);
        var self = this;
        setTimeout(function () { self.open(); });
      }
    });
  }

  function initCart() {
    qsa('[data-em-cart-open]:not([data-em-init])').forEach(function (btn) {
      btn.setAttribute('data-em-init', '');
      on(btn, 'click', function (e) {
        var d = qs('#EmCart');
        if (!d) return;
        e.preventDefault();
        d.open(btn);
      });
    });
    on(doc, 'em:cart:add', function () { EmCart.open(); });
    // quick-add buttons anywhere: <button data-em-add="variantId" data-em-add-qty="1">
    on(doc, 'click', function (e) {
      var add = e.target.closest('[data-em-add]');
      if (!add || add.closest('#EmCart')) return;
      e.preventDefault();
      if (add.classList.contains('is-loading')) return;
      add.classList.add('is-loading');
      EmCart.add(parseInt(add.getAttribute('data-em-add'), 10), parseInt(add.getAttribute('data-em-add-qty'), 10) || 1)
        .catch(function (err) { announce(err.message); var box = qs('[data-em-add-error]', add.parentElement); if (box) box.textContent = err.message; })
        .then(function () { add.classList.remove('is-loading'); });
    });
  }

  /* 7. scrollers & dots ----------------------------------------------------- */
  function initScrollers(scope) {
    qsa('[data-em-scroller]:not([data-em-init])', scope).forEach(function (root) {
      root.setAttribute('data-em-init', '');
      var track = qs('[data-em-track]', root) || root;
      var slides = qsa('[data-em-slide]', track);
      var dots = qsa('[data-em-dot]', root);
      var prev = qs('[data-em-prev]', root), next = qs('[data-em-next]', root);
      var counter = qs('[data-em-counter]', root);
      var current = 0;
      function slideWidth() { if (!slides[0]) return track.clientWidth || 1; var gap = parseFloat(getComputedStyle(track).columnGap); if (isNaN(gap)) gap = 0; return slides[0].offsetWidth + gap; }
      function mark(i) {
        current = Math.max(0, Math.min(slides.length - 1, i));
        dots.forEach(function (d, k) { d.setAttribute('aria-current', k === current ? 'true' : 'false'); });
        slides.forEach(function (s, k) { s.classList.toggle('is-current', k === current); });
        if (counter) counter.textContent = (current + 1) + ' / ' + slides.length;
        if (prev) prev.disabled = current === 0;
        if (next) next.disabled = current === slides.length - 1;
        root.dispatchEvent(new CustomEvent('em:slide', { detail: { index: current } }));
      }
      function go(i, instant) {
        var s = slides[Math.max(0, Math.min(slides.length - 1, i))];
        if (!s) return;
        var left = s.offsetLeft - track.offsetLeft;
        if (track.scrollTo) track.scrollTo({ left: left, behavior: instant || reduceMotion ? 'auto' : 'smooth' }); else track.scrollLeft = left;
        mark(i);
      }
      dots.forEach(function (d, k) { on(d, 'click', function () { go(k); }); });
      on(prev, 'click', function () { go(current - 1); });
      on(next, 'click', function () { go(current + 1); });
      var t;
      on(track, 'scroll', function () {
        clearTimeout(t);
        t = setTimeout(function () { mark(Math.round(track.scrollLeft / slideWidth())); }, 60);
      }, { passive: true });
      root.emGo = go;
      mark(0);
    });
  }

  /* 8. accordions ----------------------------------------------------------- */
  function initAccordions(scope) {
    qsa('[data-em-acc-group]:not([data-em-init])', scope).forEach(function (group) {
      group.setAttribute('data-em-init', '');
      on(group, 'toggle', function (e) {
        var d = e.target;
        if (!(d instanceof HTMLDetailsElement) || !d.open) return;
        qsa('details[open]', group).forEach(function (other) { if (other !== d && other.parentElement === d.parentElement) other.open = false; });
      }, true);
    });
  }

  /* 9. heat-level widget ---------------------------------------------------- */
  function initHeat(scope) {
    qsa('[data-em-heat]:not([data-em-init])', scope).forEach(function (root) {
      root.setAttribute('data-em-init', '');
      var steps = qsa('[data-em-heat-step]', root);
      var levelOut = qs('[data-em-heat-level]', root);
      var nameOut = qs('[data-em-heat-name]', root);
      var descOut = qs('[data-em-heat-desc]', root);
      function set(level) {
        steps.forEach(function (s) {
          var n = parseInt(s.getAttribute('data-em-heat-step'), 10);
          s.classList.toggle('is-on', n <= level);
          s.setAttribute('aria-pressed', n === level ? 'true' : 'false');
          if (n === level) {
            if (levelOut) levelOut.textContent = String(level);
            if (nameOut) nameOut.textContent = s.getAttribute('data-name') || '';
            if (descOut) descOut.textContent = s.getAttribute('data-desc') || '';
          }
        });
        root.style.setProperty('--em-heat-level', level);
      }
      steps.forEach(function (s) {
        var n = parseInt(s.getAttribute('data-em-heat-step'), 10);
        on(s, 'click', function () { set(n); });
        on(s, 'pointerenter', function (e) { if (e.pointerType === 'mouse') set(n); });
      });
      var start = parseInt(root.getAttribute('data-default'), 10) || Math.ceil(steps.length / 2);
      set(start);
      // gentle demo sweep once when it scrolls into view (not with reduced motion)
      if (!reduceMotion && 'IntersectionObserver' in window && root.getAttribute('data-demo') === 'true') {
        var io = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          var i = 1;
          var iv = setInterval(function () { set(i); i++; if (i > start) clearInterval(iv); }, 140);
        }, { threshold: 0.5 });
        io.observe(root);
      }
    });
  }

  /* boot ------------------------------------------------------------------- */
  var Emberly = window.Emberly = Object.assign(existing, {
    cart: EmCart, money: money, announce: announce, trapFocus: trapFocus, releaseFocus: releaseFocus, lockBody: lockBody,
    qs: qs, qsa: qsa, on: on, reduceMotion: reduceMotion,
    init: function (scope) { initReveal(scope); initAnnouncement(scope); initHeader(); initMenu(); initCart(); initScrollers(scope); initAccordions(scope); initHeat(scope); }
  });
  function boot() { Emberly.init(doc); }
  if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', boot); else boot();
  on(doc, 'shopify:section:load', function (e) { Emberly.init(e.target); });
  on(doc, 'shopify:section:select', function (e) {
    if (e.target && e.target.querySelector('#EmCart')) EmCart.open();
  });
  on(doc, 'shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector('#EmCart')) EmCart.close();
  });
})();
